import { Button, Stack } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import type { FormActionButtonConfig, FormActionsConfig } from '../config';

export type FormActionsProps = {
  submitLabel: string;
  cancelLabel: string;
  actions?: FormActionsConfig;
  onCancel?: () => void;
};

const isActionDisabled = (
  config: FormActionButtonConfig | undefined,
  formState: ReturnType<typeof useFormContext>['formState'],
  defaults: { submitting: boolean; invalid: boolean },
): boolean => {
  const disableWhenSubmitting = config?.disableWhenSubmitting ?? defaults.submitting;
  const disableWhenInvalid = config?.disableWhenInvalid ?? defaults.invalid;
  return (
    (disableWhenSubmitting && formState.isSubmitting) || (disableWhenInvalid && !formState.isValid)
  );
};

export const FormActions = ({ submitLabel, cancelLabel, actions, onCancel }: FormActionsProps) => {
  const { formState, reset } = useFormContext();
  const submitAction = actions?.submit;
  const cancelAction = actions?.cancel;
  const showSubmit = submitAction?.show ?? true;
  const showCancel = Boolean(onCancel) && (cancelAction?.show ?? true);

  if (!showSubmit && !showCancel) return null;

  const handleCancelClick = () => {
    if (cancelAction?.resetOnClick) {
      reset();
    }
    onCancel?.();
  };

  return (
    <Stack
      direction={{ sm: 'row', xs: 'column-reverse' }}
      spacing={actions?.spacing ?? 2}
      justifyContent="flex-end"
      sx={actions?.containerSx}
    >
      {showCancel && (
        <Button
          id={cancelAction?.id ?? 'lease-details-cancel-button'}
          data-testid={cancelAction?.dataTestId}
          onClick={handleCancelClick}
          variant={cancelAction?.variant ?? 'outlined'}
          color={cancelAction?.color ?? 'primary'}
          sx={cancelAction?.sx}
          disabled={isActionDisabled(cancelAction, formState, {
            submitting: false,
            invalid: false,
          })}
        >
          {cancelAction?.label ?? cancelLabel}
        </Button>
      )}
      {showSubmit && (
        <Button
          id={submitAction?.id ?? 'lease-details-submit-button'}
          data-testid={submitAction?.dataTestId}
          type="submit"
          variant={submitAction?.variant ?? 'contained'}
          color={submitAction?.color ?? 'primary'}
          sx={submitAction?.sx}
          disabled={isActionDisabled(submitAction, formState, { submitting: true, invalid: false })}
        >
          {submitAction?.label ?? submitLabel}
        </Button>
      )}
    </Stack>
  );
};
